import { useEffect, useState } from "react";
import {
    FaUserCircle,
    FaLock,
    FaSave
} from "react-icons/fa";

import Sidebar from "../components/Sidebar";
import Topbar from "../components/Topbar";
import { getProfile, updateProfile, changePassword } from "../services/auth";

import "../styles/profile.css";

export default function Settings() {

    const [menuOpen, setMenuOpen] = useState(false);

    // ---------------- Profile form ----------------
    const [fullName, setFullName] = useState("");
    const [email, setEmail] = useState("");
    const [phone, setPhone] = useState("");
    const [address, setAddress] = useState("");

    const [loadingProfile, setLoadingProfile] = useState(true);
    const [savingProfile, setSavingProfile] = useState(false);
    const [profileMsg, setProfileMsg] = useState(null); // { type: 'ok'|'error', text }

    // ---------------- Password form ----------------
    const [currentPassword, setCurrentPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [savingPassword, setSavingPassword] = useState(false);
    const [passwordMsg, setPasswordMsg] = useState(null);

    useEffect(() => {

        getProfile()
            .then(res => {

                const u = res.data.user;
                setFullName(u.full_name || "");
                setEmail(u.email || "");
                setPhone(u.phone || "");
                setAddress(u.address || "");

            })
            .catch(err => {

                setProfileMsg({
                    type: "error",
                    text: err.response?.data?.message || "Couldn't load your profile."
                });

            })
            .finally(() => setLoadingProfile(false));

    }, []);

    async function handleSaveProfile(e) {

        e.preventDefault();
        setSavingProfile(true);
        setProfileMsg(null);

        try {

            const res = await updateProfile({
                full_name: fullName,
                email,
                phone,
                address
            });

            setFullName(res.data.user.full_name);
            setEmail(res.data.user.email);

            // Topbar reads this key directly from localStorage
            localStorage.setItem("userName", res.data.user.full_name);
            localStorage.setItem("userEmail", res.data.user.email);

            setProfileMsg({ type: "ok", text: "Profile updated." });

        } catch (err) {

            setProfileMsg({
                type: "error",
                text: err.response?.data?.message || "Couldn't save your profile."
            });

        } finally {

            setSavingProfile(false);

        }

    }

    async function handleChangePassword(e) {

        e.preventDefault();
        setPasswordMsg(null);

        if (newPassword !== confirmPassword) {

            setPasswordMsg({ type: "error", text: "New passwords don't match." });
            return;

        }

        setSavingPassword(true);

        try {

            await changePassword({ currentPassword, newPassword });

            setPasswordMsg({ type: "ok", text: "Password updated." });
            setCurrentPassword("");
            setNewPassword("");
            setConfirmPassword("");

        } catch (err) {

            setPasswordMsg({
                type: "error",
                text: err.response?.data?.message || "Couldn't change your password."
            });

        } finally {

            setSavingPassword(false);

        }

    }

    return (

        <div className="layout">

            <Sidebar menuOpen={menuOpen} setMenuOpen={setMenuOpen} />

            <div className="content">

                <Topbar
                    setMenuOpen={setMenuOpen}
                    title="My Profile"
                    subtitle="Account settings"
                />

                <div className="profileBody">

                    {/* ---------- Profile details ---------- */}
                    <form className="profileCard" onSubmit={handleSaveProfile}>

                        <h2><FaUserCircle /> Profile Details</h2>

                        {loadingProfile ? (

                            <p className="profileHint">Loading...</p>

                        ) : (

                            <>

                                <label>Full Name</label>
                                <input
                                    type="text"
                                    value={fullName}
                                    onChange={(e) => setFullName(e.target.value)}
                                    required
                                />

                                <label>Email</label>
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                />

                                <label>Phone</label>
                                <input
                                    type="tel"
                                    placeholder="e.g. +92 300 1234567"
                                    value={phone}
                                    onChange={(e) => setPhone(e.target.value)}
                                />

                                <label>Address</label>
                                <textarea
                                    rows={2}
                                    placeholder="Street, city, postal code"
                                    value={address}
                                    onChange={(e) => setAddress(e.target.value)}
                                />

                                {profileMsg && (
                                    <div className={`profileMsg ${profileMsg.type}`}>
                                        {profileMsg.text}
                                    </div>
                                )}

                                <button type="submit" disabled={savingProfile}>
                                    <FaSave />
                                    {savingProfile ? "Saving..." : "Save Changes"}
                                </button>

                            </>

                        )}

                    </form>

                    {/* ---------- Change password ---------- */}
                    <form className="profileCard" onSubmit={handleChangePassword}>

                        <h2><FaLock /> Change Password</h2>

                        <label>Current Password</label>
                        <input
                            type="password"
                            value={currentPassword}
                            onChange={(e) => setCurrentPassword(e.target.value)}
                            required
                        />

                        <label>New Password</label>
                        <input
                            type="password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            minLength={8}
                            required
                        />

                        <label>Confirm New Password</label>
                        <input
                            type="password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            minLength={8}
                            required
                        />

                        {passwordMsg && (
                            <div className={`profileMsg ${passwordMsg.type}`}>
                                {passwordMsg.text}
                            </div>
                        )}

                        <button type="submit" disabled={savingPassword}>
                            <FaLock />
                            {savingPassword ? "Updating..." : "Update Password"}
                        </button>

                    </form>

                </div>

            </div>

        </div>

    );

}
